import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const WidgetLibrary = ({ onWidgetDrag }) => {
  const [activeCategory, setActiveCategory] = useState('author');

  const widgetCategories = [
    {
      id: 'author',
      name: 'Author',
      icon: 'User',
      widgets: [
        {
          id: 'author-bio',
          name: 'Author Bio Card',
          icon: 'FileText',
          description: 'Professional author biography with photo'
        },
        {
          id: 'author-headshot',
          name: 'Author Headshot',
          icon: 'Camera',
          description: 'High-resolution author photo'
        },
        {
          id: 'author-quote',
          name: 'Author Quote',
          icon: 'Quote',
          description: 'Featured quote or testimonial'
        }
      ]
    },
    {
      id: 'book',
      name: 'Book',
      icon: 'Book',
      widgets: [
        {
          id: 'book-synopsis',
          name: 'Book Synopsis',
          icon: 'FileText',
          description: 'Detailed book description and summary'
        },
        {
          id: 'book-cover',
          name: 'Book Cover',
          icon: 'Image',
          description: 'High-resolution book cover display'
        },
        {
          id: 'book-details',
          name: 'Book Details',
          icon: 'Info',
          description: 'Publication info, ISBN, genre'
        }
      ]
    },
    {
      id: 'media',
      name: 'Media',
      icon: 'Play',
      widgets: [
        {
          id: 'video-player',
          name: 'Video Player',
          icon: 'PlayCircle',
          description: 'Embedded video for interviews or trailers'
        },
        {
          id: 'audio-player',
          name: 'Audio Player',
          icon: 'Headphones',
          description: 'Audio clips or book excerpts'
        },
        {
          id: 'gallery',
          name: 'Image Gallery',
          icon: 'Images',
          description: 'Collection of promotional images'
        }
      ]
    },
    {
      id: 'assets',
      name: 'Assets',
      icon: 'Download',
      widgets: [
        {
          id: 'download-section',
          name: 'Download Section',
          icon: 'Download',
          description: 'Downloadable press materials'
        },
        {
          id: 'sample-chapters',
          name: 'Sample Chapters',
          icon: 'BookOpen',
          description: 'Preview chapters for media'
        },
        {
          id: 'press-images',
          name: 'Press Images',
          icon: 'ImageIcon',
          description: 'High-res promotional images'
        }
      ]
    },
    {
      id: 'company',
      name: 'Company',
      icon: 'Building',
      widgets: [
        {
          id: 'company-info',
          name: 'Company Info',
          icon: 'Building2',
          description: 'Publisher information and facts'
        },
        {
          id: 'media-logos',
          name: 'Media Logos',
          icon: 'Award',
          description: 'Featured media outlet logos'
        },
        {
          id: 'reviews',
          name: 'Reviews',
          icon: 'Star',
          description: 'Published reviews and testimonials'
        }
      ]
    }
  ];

  const handleDragStart = (e, widget) => {
    e?.dataTransfer?.setData('application/json', JSON.stringify(widget));
    e.dataTransfer.effectAllowed = 'copy';
    if (onWidgetDrag) {
      onWidgetDrag(widget);
    }
  };

  return (
    <div className="w-80 bg-card border-r border-border h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <h2 className="text-lg font-semibold text-foreground flex items-center space-x-2">
          <Icon name="Package" size={20} />
          <span>Widget Library</span>
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Drag widgets to build your press kit
        </p>
      </div>
      {/* Categories */}
      <div className="flex-1 overflow-hidden flex flex-col">
        <div className="p-4 border-b border-border">
          <div className="grid grid-cols-3 gap-2">
            {widgetCategories?.map((category) => (
              <button
                key={category?.id}
                onClick={() => setActiveCategory(category?.id)}
                className={`flex flex-col items-center p-3 rounded-lg text-xs font-medium transition-colors duration-200 ${
                  activeCategory === category?.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
                }`}
              >
                <Icon name={category?.icon} size={16} className="mb-1" />
                <span>{category?.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Widgets */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-3">
            {widgetCategories?.find(cat => cat?.id === activeCategory)
              ?.widgets?.map((widget) => (
                <div
                  key={widget?.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, widget)}
                  className="bg-muted/50 border border-border rounded-lg p-4 cursor-grab hover:bg-muted hover:border-primary/50 transition-all duration-200 active:cursor-grabbing"
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name={widget?.icon} size={18} className="text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-foreground mb-1">
                        {widget?.name}
                      </h4>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {widget?.description}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                      <Icon name="Move" size={12} />
                      <span>Drag to add</span>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WidgetLibrary;